import { useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "@/context/AuthContext"
import {
  connectBattleSocket,
  consumePendingBattleStarted,
  consumePendingDeckBattleResume,
  consumePendingDeckBattleStarted,
  onBattleEvent,
  requestDeckBattleRestore,
} from "@/socket/battleSocket"
import { normalizeDeckBattleResumePayload } from "@/utils/deckPayload"
import { deckResumeToStarted } from "@/features/battle/deckResumeToStarted"

const SKIP_PREFIXES = [
  "/play/duel",
  "/play/deck-duel",
  "/play/onboarding",
  "/login",
  "/verify",
]

function shouldSkipReconnect(path: string): boolean {
  return SKIP_PREFIXES.some((p) => path.startsWith(p))
}

/** После входа в /play — восстановить активный 1×1 или колодный бой. */
export default function BattleReconnectGate() {
  const { token, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const ranRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !token || ranRef.current) return
    if (shouldSkipReconnect(location.pathname)) return

    ranRef.current = true

    void (async () => {
      try {
        await connectBattleSocket(token)
      } catch {
        return
      }

      const duel = consumePendingBattleStarted()
      if (duel) {
        navigate("/play/duel", { state: { battle: duel }, replace: true })
        return
      }

      const deckStarted = consumePendingDeckBattleStarted()
      if (deckStarted) {
        navigate("/play/deck-duel", {
          state: { battle: deckStarted },
          replace: true,
        })
        return
      }

      let resume = consumePendingDeckBattleResume()
      if (!resume) {
        const ack = await requestDeckBattleRestore(token)
        const raw = ack?.battle
        resume = raw ? normalizeDeckBattleResumePayload(raw) : null
      }

      if (resume) {
        navigate("/play/deck-duel", {
          state: { battle: deckResumeToStarted(resume), resume },
          replace: true,
        })
      }
    })()

    const offResume = onBattleEvent("deck-battle-resume", (payload) => {
      const normalized = normalizeDeckBattleResumePayload(payload)
      if (!normalized) return
      if (location.pathname.startsWith("/play/deck-duel")) return
      navigate("/play/deck-duel", {
        state: { battle: deckResumeToStarted(normalized), resume: normalized },
      })
    })

    const offStarted = onBattleEvent("battle-started", (payload) => {
      if (location.pathname.startsWith("/play/duel")) return
      navigate("/play/duel", { state: { battle: payload } })
    })

    const offDeckStarted = onBattleEvent("deck-battle-started", (payload) => {
      if (location.pathname.startsWith("/play/deck-duel")) return
      navigate("/play/deck-duel", { state: { battle: payload } })
    })

    return () => {
      offResume()
      offStarted()
      offDeckStarted()
    }
  }, [isAuthenticated, token, navigate, location.pathname])

  return null
}
